import { close } from '@/features/sidebar';
import { useSupportChat } from '@/layouts/authenticated/components/top-navigation/components/support-chat/hook';
import { useAnalytics } from '@/services/analytics';
import { useCopilotTool } from '@customerio/hydra-copilot/react';
import { Button } from '@customerio/pluma-components/react';
import { z } from 'zod';
import zodToJsonSchema from 'zod-to-json-schema';

export function ShowSupportFormTool() {
  const { openChat, isHandoverAllowed } = useSupportChat();
  const { track } = useAnalytics();

  useCopilotTool({
    type: 'client',
    name: 'show_support_form',
    description: `
      Shows the inline support form for the user to get help. Use this ONLY when the user explicitly asks for help from a human. This is not for general feedback or feature requests, but rather for direct support requests.

      This should be used when the user explicitly asks for a human to help them. Never suggest this tool unless the user has asked for help from a human.

      Do everything in your power to help the user, but if you cannot help them, use this tool to connect them to support.

      Confirm the details of the support request with the user before showing the form if the user has not provided enough information.

      <sample-chat>
      ...continuation of the chat...
      User: Talk to a human
      Model: Sure, I can connect you to support.
      Model: show_support_form({ subject: "Account Settings Help", body: "I need help configuring my account settings but I'm not sure where to find the options I need. My goal is to update my notification preferences and user permissions." })
      </sample-chat>
      
      <sample-chat>
      ...continuation of the chat...
      User: I need help with my account settings, talk to a human.
      Model: Ok, let me connect you to support.
      Model: show_support_form({ subject: "Account Settings Help", body: "I need assistance with account settings configuration but haven't been able to find the right options in the interface. I want to update my settings but need guidance on where to locate them." })
      </sample-chat>

      <sample-chat>
      ...continuation of the chat...
      User: I have a problem with my subscription, can you help me?
      Model: I can't help you with that
      User: I need to talk to someone
      Model: Ok, let me connect you to support. If I understand correctly, you need help with your subscription.
      User: Yes, I need help with that.
      Model: show_support_form({ subject: "Subscription Issue", body: "I am experiencing an issue with my subscription that the assistant couldn't resolve. I need help troubleshooting the problem and getting my subscription working properly." })
      </sample-chat>

      <sample-chat>
      ...continuation of the chat...
      User: My campaigns aren't sending and I'm getting a 403 error
      Model: Let me help you troubleshoot this. Have you checked your sending permissions?
      User: Yes, I checked and they look correct. I also tried refreshing the page
      Model: Can you try clearing your browser cache?
      User: I did that too, still getting the same error. Can I talk to someone?
      Model: Let me connect you to support since we've tried the basic troubleshooting steps.
      Model: show_support_form({ subject: "Campaign Sending Error", body: "I am unable to send campaigns and receiving a 403 error when attempting to do so. I have already checked my sending permissions which appear correct, refreshed the page, and cleared my browser cache, but the error persists. I need to get my campaigns sending again for my marketing schedule." })
      </sample-chat>
    `,
    requestSchema: zodToJsonSchema(
      z.object({
        subject: z
          .string()
          .describe(
            'The subject line for the support request, typically describing the type of help needed (e.g., "Feature Request", "Account Help", etc.)',
          ),
        body: z
          .string()
          .describe(
            'A comprehensive summary of what the user needs help with, written in first person. Include: 1) The specific problem or question they have, 2) What they\'ve already tried or checked, 3) Any error messages or symptoms, 4) Their goal or desired outcome. Synthesize the entire conversation context into a clear, actionable summary that gives support agents the full context without needing to read the chat history. Example: "I am trying to set up email campaigns but getting a 403 error when accessing the campaigns page. I have checked my permissions and tried clearing my browser cache, but the issue persists. I need to be able to create and send campaigns for my marketing team."',
          ),
      }),
    ),
    handler() {
      return 'button displayed in chat, the user must click it to open the support form';
    },
    render({ args, session }) {
      const { subject, body } = args;

      return (
        <div>
          <Button
            onClick={() => {
              if (isHandoverAllowed === true) {
                openChat(`[ASSISTANT TRANSFER ${session.id}] ${body}`);
                close();
                track('aiAssistant', 'transferredToTsChat', {});
                return true;
              } else {
                if (typeof window !== 'undefined' && 'showInlineSupport' in window) {
                  (
                    window as unknown as { showInlineSupport: (subject: string, body: string) => void }
                  ).showInlineSupport(`[ASSISTANT TRANSFER ${session.id}] ${subject}`, body);
                  track('aiAssistant', 'transferredToTsSupportForm', {});
                  return true;
                }

                throw new Error('showInlineSupport is not available in the current environment');
              }
            }}
          >
            {isHandoverAllowed === true ? 'Chat with Support' : 'Open Support Form'}
          </Button>
        </div>
      );
    },
  });

  return null;
}
